import { ShareScreenBase } from "./share-screen-base"
import { getRoomDeeplinkUrl, isMobile, isUsOrCaTimezone } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { RoomContentCard } from "./room-content-card"
import {
  getEpisodeData,
  getInviterData,
  getMessageData,
  getRoomData,
} from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useRoomShareEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"

export const ShareMessageScreen = (): JSX.Element => {
  const messageData = getMessageData()
  const episodeData = getEpisodeData()
  const roomData = getRoomData()
  const collectEmail = useCollectEmail()

  // The shared item this screen is unfurling (server-rendered). item_type grows
  // as more shareable content types are added (live spaces, games, ...).
  const sharedItem = messageData
    ? { item_type: "message", item_id: messageData.id }
    : episodeData
      ? { item_type: "episode", item_id: episodeData.id }
      : undefined

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const phoneSubmission = useRoomSharePhoneSubmission()
  const emailSubmission = useRoomShareEmailSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
    ...sharedItem,
  })

  const handleJoinRoom = useCallback(async () => {
    const successMessage =
      isMobile() || !isUsOrCaTimezone()
        ? "Download the app to join the group chat."
        : collectEmail
          ? "Check your email for a link to download the app."
          : "Check your texts for a link to download the app."

    const successTitle = "You skipped the waitlist!"
    const inviterData = getInviterData()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      room_id: roomId,
      deeplink_url: deeplinkUrl,
      room_handle: roomHandle,
    }

    if (collectEmail) {
      await emailSubmission.submitEmail({ successMessage, successTitle, roomId, trackingData })
    } else {
      await phoneSubmission.submitPhoneNumber({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    }
  }, [collectEmail, emailSubmission, phoneSubmission, roomId, deeplinkUrl, roomHandle])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      handleJoinRoom={handleJoinRoom}
      deeplinkUrl={deeplinkUrl}
      {...inputProps}
      renderContent={() =>
        roomData && <RoomContentCard roomData={roomData} className="max-w-[640px]" />
      }
    />
  )
}

export default ShareMessageScreen
